/**
 * @file price-history-chart.component.ts
 * Graphique ligne minimaliste (SVG inline, pas de lib externe) montrant
 * l'évolution du prix des dernières ventes d'une carte.
 *
 * Attend un tableau déjà trié chronologiquement (ancien → récent) — le tri
 * DESC renvoyé par l'API doit être reversé par l'appelant.
 */

import { ChangeDetectionStrategy, Component, ElementRef, computed, input, signal, viewChild } from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';
import { LocalizedDatePipe } from '@/app/shared/pipes/localizedDate.pipe';
import { SaleRecord } from '@/app/core/models/marketplace.model';

interface ChartPoint {
	x: number;
	y: number;
	sale: SaleRecord;
}

const VB_W = 640;
const VB_H = 256;
const PAD_L = 60;
const PAD_R = 16;
const PAD_T = 16;
const PAD_B = 40;
const PLOT_W = VB_W - PAD_L - PAD_R;
const PLOT_H = VB_H - PAD_T - PAD_B;
const BASE_Y = PAD_T + PLOT_H;

@Component({
	selector: 'peps-price-history-chart',
	standalone: true,
	imports: [LocalizedDatePipe, TranslatePipe],
	templateUrl: './price-history-chart.component.html',
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PriceHistoryChartComponent {
	/** Ventes triées ancien → récent (voir note en tête de fichier). */
	sales = input.required<SaleRecord[]>();

	private readonly svgRef = viewChild<ElementRef<SVGSVGElement>>('svg');

	readonly viewBox = `0 0 ${VB_W} ${VB_H}`;
	readonly baseY = BASE_Y;
	readonly plotLeft = PAD_L;
	readonly plotRight = VB_W - PAD_R;
	readonly plotTop = PAD_T;

	hoverIndex = signal<number | null>(null);

	private readonly priceRange = computed(() => {
		const prices = this.sales().map(s => s.price);
		const min = Math.min(...prices);
		const max = Math.max(...prices);
		// Évite une échelle plate si toutes les ventes sont au même prix.
		return min === max ? { min: min - 1, max: max + 1 } : { min, max };
	});

	readonly points = computed<ChartPoint[]>(() => {
		const sales = this.sales();
		const n = sales.length;
		if (n === 0) return [];
		const { min, max } = this.priceRange();
		return sales.map((sale, i) => ({
			x: n === 1 ? PAD_L + PLOT_W / 2 : PAD_L + (i / (n - 1)) * PLOT_W,
			y: PAD_T + PLOT_H - ((sale.price - min) / (max - min)) * PLOT_H,
			sale,
		}));
	});

	readonly linePath = computed(() => {
		const pts = this.points();
		if (!pts.length) return '';
		return pts.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x.toFixed(1)} ${p.y.toFixed(1)}`).join(' ');
	});

	readonly areaPath = computed(() => {
		const pts = this.points();
		if (!pts.length) return '';
		const first = pts[0];
		const last = pts[pts.length - 1];
		return `${this.linePath()} L ${last.x.toFixed(1)} ${BASE_Y} L ${first.x.toFixed(1)} ${BASE_Y} Z`;
	});

	/** 3 lignes de repère (max / milieu / min) avec leur prix. */
	readonly gridLines = computed(() => {
		const { min, max } = this.priceRange();
		const mid = (min + max) / 2;
		return [
			{ y: PAD_T, price: Math.round(max) },
			{ y: PAD_T + PLOT_H / 2, price: Math.round(mid) },
			{ y: BASE_Y, price: Math.round(min) },
		];
	});

	readonly hoverPoint = computed(() => {
		const i = this.hoverIndex();
		return i === null ? null : this.points()[i] ?? null;
	});

	/** Résumé texte pour lecteurs d'écran — le SVG seul n'est pas exploitable au clavier. */
	readonly ariaSummary = computed(() => {
		const sales = this.sales();
		if (!sales.length) return '';
		const { min, max } = this.priceRange();
		return `${sales.length} ventes, de ${Math.round(min)} à ${Math.round(max)} PepsCoins`;
	});

	onMouseMove(event: MouseEvent): void {
		const svg = this.svgRef()?.nativeElement;
		const pts = this.points();
		if (!svg || !pts.length) return;

		const rect = svg.getBoundingClientRect();
		const ratio = VB_W / rect.width;
		const xInViewBox = (event.clientX - rect.left) * ratio;

		let nearest = 0;
		let nearestDist = Infinity;
		for (let i = 0; i < pts.length; i++) {
			const dist = Math.abs(pts[i].x - xInViewBox);
			if (dist < nearestDist) {
				nearestDist = dist;
				nearest = i;
			}
		}
		this.hoverIndex.set(nearest);
	}

	onMouseLeave(): void {
		this.hoverIndex.set(null);
	}

	/** Position CSS (%) du point survolé, pour placer le tooltip HTML au-dessus du SVG. */
	pointLeftPct(p: ChartPoint): number {
		return (p.x / VB_W) * 100;
	}

	pointTopPct(p: ChartPoint): number {
		return (p.y / VB_H) * 100;
	}
}
